import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import ChartCanvas from './ChartCanvas';
import CoinSearch from './CoinSearch';
import { Grid, X, Loader2, Search } from 'lucide-react';
import { useBinanceKline } from '../../lib/crypto/useBinanceStream';

const TFS = ['5m', '15m', '30m', '1h', '4h', '1d'];
const LAYOUTS = [
  { key: '1x2', label: '1x2', cols: 2, rows: 1, count: 2 },
  { key: '2x2', label: '2x2', cols: 2, rows: 2, count: 4 },
  { key: '2x3', label: '2x3', cols: 3, rows: 2, count: 6 },
];

/**
 * ChartPane — one panel of the multi-chart grid. Loads its own OHLC
 * data but reads the shared `tf` from the parent so all panels stay
 * in sync when the operator changes timeframe.
 */
function ChartPane({ symbol, base, quote, tf, indicators, onChange }) {
  const [candles, setCandles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPick, setShowPick] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const { data } = await api.get(`/crypto/ohlc/${symbol}`, {
          params: { tf, limit: 1000 },
        });
        if (!cancelled) setCandles(data?.candles || []);
      } catch { if (!cancelled) setCandles([]); }
      finally { if (!cancelled) setBusy(false); }
    })();
    // Safety-net refresh every 45s — WS handles live ticks.
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/crypto/ohlc/${symbol}`, {
          params: { tf, limit: 1000 },
        });
        if (!cancelled) setCandles(data?.candles || []);
      } catch { /* noop */ }
    }, 45000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [symbol, tf]);

  // Live Binance kline — patches the current candle for this pane.
  // (No Coinbase-trade snap: keeps the pane bit-identical to the
  //  Binance exchange chart.)
  const liveKline = useBinanceKline(symbol, tf);
  useEffect(() => {
    if (!liveKline) return;
    setCandles((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      const next = [...prev];
      if (last.time === liveKline.time) {
        next[next.length - 1] = { ...last, ...liveKline };
      } else if (liveKline.time > last.time) {
        next.push(liveKline);
        if (next.length > 1000) next.shift();
      }
      return next;
    });
  }, [liveKline]);

  const last = candles.length ? candles[candles.length - 1].close : null;
  const first = candles.length ? candles[0].open : null;
  const chg = (last && first) ? ((last - first) / first) * 100 : 0;

  return (
    <div className="flex flex-col border border-[var(--border-default)] bg-black min-h-0"
         data-testid={`multichart-pane-${symbol}`}>
      <div className="flex items-center gap-2 px-2 py-1 border-b border-[var(--border-default)] bg-[var(--bg-panel)]">
        <button
          onClick={() => setShowPick((v) => !v)}
          data-testid={`multichart-pick-${symbol}`}
          className="flex items-center gap-1 text-[11px] font-mono text-cyan-sys hover:text-white"
        >
          <Search size={10} /> {base}/{quote}
        </button>
        <span className="text-[9px] tracking-widest uppercase text-[var(--txt-muted)]">{tf}</span>
        {last && (
          <>
            <span className="text-[10px] font-mono tabular-nums ml-auto">${last.toFixed(last < 1 ? 5 : 2)}</span>
            <span className={`text-[9px] font-mono tabular-nums ${chg >= 0 ? 'text-buy' : 'text-sell'}`}>
              {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
            </span>
          </>
        )}
      </div>
      {showPick && (
        <div className="absolute z-30 mt-8 bg-black border border-cyan-sys p-2 w-80 shadow-lg">
          <CoinSearch
            onPick={(row) => { onChange?.(row); setShowPick(false); }}
            current={base}
          />
          <button
            onClick={() => setShowPick(false)}
            data-testid="multichart-pick-close"
            className="mt-2 text-[9px] tracking-widest uppercase text-[var(--txt-muted)] hover:text-cyan-sys flex items-center gap-1"
          ><X size={10} /> close</button>
        </div>
      )}
      <div className="flex-1 min-h-0 relative">
        {busy && candles.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 size={16} className="animate-spin text-cyan-sys" />
          </div>
        )}
        <ChartCanvas
          candles={candles}
          type="candles"
          indicators={indicators}
        />
      </div>
    </div>
  );
}

/**
 * MultiChart — Phase 53.D. Grid of synchronized crypto charts. Coin
 * per pane, one shared timeframe.
 */
export default function MultiChart({ initial = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT'] }) {
  const [layout, setLayout] = useState('2x2');
  const [tf, setTf] = useState('1h');
  // Main-chart indicator preferences — surface them here so a single
  // toggle updates every pane in the multi-chart grid. Falls back to
  // a sensible default when the pref API is unreachable.
  const [indicators, setIndicators] = useState({ ema20: true, ema50: true, ema200: true });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/crypto/prefs');
        const src = data?.indicators || {};
        // Only surface overlay-style indicators in MultiChart. Pane
        // indicators (RSI/MACD/etc) don't render inside a grid pane.
        const overlays = ['ema9','ema20','ema21','ema50','ema100','ema200','ema300','ema500','vwap','bb','supertrend'];
        const next = {};
        for (const k of overlays) if (src[k]) next[k] = true;
        // Guarantee at least one EMA is on so newcomers see context.
        if (!Object.keys(next).length) { next.ema20 = true; next.ema50 = true; }
        setIndicators(next);
      } catch { /* keep defaults */ }
    })();
  }, []);
  const [panes, setPanes] = useState(() => {
    // Guarantee at least 6 panes on mount so switching to the 2x3
    // layout paints headers immediately — the previous version
    // padded lazily and briefly rendered blank rows.
    const seed = initial.slice(0, 6);
    while (seed.length < 6) seed.push('BTCUSDT');
    return seed.map((sym) => ({
      symbol: sym,
      base:   sym.replace(/USDT|USDC|USD$/, ''),
      quote:  sym.endsWith('USDC') ? 'USDC' : 'USDT',
    }));
  });
  const cfg = LAYOUTS.find((l) => l.key === layout);

  const setPane = (i, row) => {
    const next = [...panes];
    next[i] = { symbol: row.symbol, base: row.base, quote: row.quote };
    setPanes(next);
  };

  // Trim / pad panes when the layout changes.
  useEffect(() => {
    if (panes.length < cfg.count) {
      // pad with the last pane's coin as a placeholder
      const pad = [];
      const pool = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT'];
      for (let i = panes.length; i < cfg.count; i++) {
        const sym = pool[i % pool.length];
        pad.push({ symbol: sym, base: sym.replace(/USDT|USDC|USD$/, ''), quote: 'USDT' });
      }
      setPanes([...panes, ...pad]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const visible = panes.slice(0, cfg.count);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="crypto-multichart">
      <div className="flex items-center gap-3 border-b border-[var(--border-default)] bg-[var(--bg-panel)] px-3 py-2">
        <Grid size={14} className="text-cyan-sys" />
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys">// multi-chart</div>

        <div className="flex items-center gap-1 ml-3">
          <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">LAYOUT</span>
          {LAYOUTS.map((l) => (
            <button
              key={l.key}
              onClick={() => setLayout(l.key)}
              data-testid={`multichart-layout-${l.key}`}
              className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${layout === l.key ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
            >{l.label}</button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">TF</span>
          {TFS.map((x) => (
            <button
              key={x}
              onClick={() => setTf(x)}
              data-testid={`multichart-tf-${x}`}
              className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${tf === x ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
            >{x}</button>
          ))}
        </div>
      </div>

      <div
        className="flex-1 min-h-0 grid gap-1 p-1"
        style={{
          gridTemplateColumns: `repeat(${cfg.cols}, minmax(0, 1fr))`,
          gridTemplateRows:    `repeat(${cfg.rows}, minmax(0, 1fr))`,
        }}
      >
        {visible.map((p, i) => (
          <ChartPane
            key={`${p.symbol}-${i}`}
            symbol={p.symbol}
            base={p.base}
            quote={p.quote}
            tf={tf}
            indicators={indicators}
            onChange={(row) => setPane(i, row)}
          />
        ))}
      </div>
    </div>
  );
}
